/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { SxProps } from "@mui/material";
import { IFilterSelectBox } from "../../Shared/Filter/types";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { setSelectedReportTask, setSelectedReportType } from "../../../store/filters/Reports/reducers";
import { setRegionFilterIds } from "../../../store/filters/Nodes/reducer";
import { reportFilterSelector } from "../../../store/filters/Reports/selectors";
import { devicesSelector } from "../../../store/filters/Nodes/selectors";
import { useGetReportListQuery, useGetReportTaskListQuery } from "../../../store/api/tasks/taskApiSlice";
import { selectedGeoRegionsSelector, selectedGeoRegionIdsSelector } from "../../../store/api/admin/selectors";
import { IGeoRegion } from "../../../store/api/admin/types";
import { useRegionDisplay } from "../../../pages/Administrations/AccessControls/Regions/hooks";
import { useMemo } from "react";

export const useReportsFilter = () => {
  const dispatch = useStoreDispatch();
  const selectedGeoRegions = useSelector(selectedGeoRegionsSelector) as IGeoRegion[];
  const reportFilterData = useSelector(reportFilterSelector);
  const deviceFilterData = useSelector(devicesSelector);
  const reportList = useGetReportListQuery();
  
  const { geoRegions } = useRegionDisplay();
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);

  const regionOptions = useMemo(() => {
    if (!geoRegions || geoRegions.length === 0) return [];

    const baseRegions = selectedGeoRegionIds && selectedGeoRegionIds.length
      ? geoRegions.filter((region: IGeoRegion) => selectedGeoRegionIds.includes(region.objectKey))
      : geoRegions;

    return baseRegions
      .map((region: IGeoRegion) => ({ value: region.objectKey, text: region.name }))
      .sort((a, b) => (a.text || "").localeCompare(b.text || ""));
  }, [geoRegions, selectedGeoRegionIds]);

  const regionNames = (selectedGeoRegions || [])
    .map((region) => region?.name)
    .filter((name): name is string => !!name && name.trim().length > 0);

  const geoRegionArg = regionNames.length ? regionNames.join(",") : "";

  const reportTaskList = useGetReportTaskListQuery(geoRegionArg);

  const nodeRegionFilter: IFilterSelectBox[] = [
    {
      id: "regionFilter",
      label: "REGION",
      value: deviceFilterData.regionFilterIds || [],
      setValue: setRegionFilterIds,
      values: regionOptions,
      width: "200px",
      multiple: true,
    }
  ];

  const filters: IFilterSelectBox[] = [
    ...nodeRegionFilter,
    {
      id: "type",
      label: "TYPE",
      value: reportFilterData?.selectedReportType || "",
      setValue: setSelectedReportType,
      values: reportList?.data || [],
      width: "140px",
    },
    {
      id: "task",
      label: "TASK",
      value: reportFilterData?.selectedReportTask || "",
      setValue: setSelectedReportTask,
      values: reportTaskList?.data || [],
      width: "140px",
    },
  ];

  const formControlSx: SxProps = { minWidth: 100, marginRight: "10px" };
  const searchTooltip = "Search by 'Report Name', 'Type',or  'Tasks', fields for quick access.";
  return {
    dispatch,
    filters,
    formControlSx,
    searchTooltip,
  };
};
